import React, { useEffect, useState, useRef } from 'react';
import client from '../../../api/client';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function AttendanceSettings() {
  const [schedule, setSchedule] = useState({ working_days: [0,1,2,3,4], work_start_time: '09:00', work_end_time: '18:00' });
  const [schedSaving, setSchedSaving] = useState(false);
  const [schedSaved, setSchedSaved] = useState(false);

  const [locations, setLocations] = useState([]);
  const [selectedLoc, setSelectedLoc] = useState('');
  const [holidays, setHolidays] = useState([]);
  const [newDate, setNewDate] = useState('');
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);

  const [uploadLoc, setUploadLoc] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    client.get('/config/system/').then(r => setSchedule(r.data)).catch(() => {});
    client.get('/locations/').then(r => setLocations(r.data.results || r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const url = '/config/holidays/' + (selectedLoc ? `?location=${selectedLoc}` : '');
    client.get(url).then(r => setHolidays(r.data)).catch(() => {});
  }, [selectedLoc]);

  const toggleDay = (day) => {
    setSchedule(s => ({
      ...s,
      working_days: s.working_days.includes(day)
        ? s.working_days.filter(d => d !== day)
        : [...s.working_days, day].sort((a, b) => a - b),
    }));
  };

  const saveSchedule = async () => {
    setSchedSaving(true);
    await client.patch('/config/system/', schedule);
    setSchedSaving(false);
    setSchedSaved(true);
    setTimeout(() => setSchedSaved(false), 2000);
  };

  const addHoliday = async () => {
    if (!newDate || !newName) return;
    setAdding(true);
    try {
      const res = await client.post('/config/holidays/', {
        location: selectedLoc || null,
        date: newDate,
        name: newName,
      });
      setHolidays(h => [...h, res.data].sort((a, b) => a.date.localeCompare(b.date)));
      setNewDate('');
      setNewName('');
    } catch {}
    setAdding(false);
  };

  const deleteHoliday = async (id) => {
    await client.delete(`/config/holidays/${id}/`);
    setHolidays(h => h.filter(x => x.id !== id));
  };

  const handleUpload = async () => {
    const file = fileRef.current?.files[0];
    if (!file) return;
    setUploading(true);
    setUploadResult(null);
    const form = new FormData();
    form.append('file', file);
    if (uploadLoc) form.append('location', uploadLoc);
    try {
      const res = await client.post('/config/holidays/upload/', form);
      setUploadResult(res.data);
      const url = '/config/holidays/' + (selectedLoc ? `?location=${selectedLoc}` : '');
      client.get(url).then(r => setHolidays(r.data)).catch(() => {});
    } catch {}
    setUploading(false);
  };

  return (
    <div className="space-y-6">
      {/* Working Schedule */}
      <div className="card p-6 space-y-5">
        <div>
          <h2 className="font-bold text-brand-500 text-sm">Working Schedule</h2>
          <p className="text-xs text-gray-400 mt-0.5">Used to calculate RE absence days and attendance penalties</p>
        </div>
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Working Days</p>
          <div className="flex gap-2 flex-wrap">
            {DAY_LABELS.map((label, i) => (
              <button
                key={i}
                onClick={() => toggleDay(i)}
                className={`w-12 h-12 rounded-xl text-sm font-bold transition-colors ${
                  schedule.working_days.includes(i)
                    ? 'bg-brand-500 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-end gap-6 flex-wrap">
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Work Start Time</label>
            <input
              type="time"
              value={schedule.work_start_time || '09:00'}
              onChange={e => setSchedule(s => ({ ...s, work_start_time: e.target.value }))}
              className="input-field w-36"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Work End Time</label>
            <input
              type="time"
              value={schedule.work_end_time || '18:00'}
              onChange={e => setSchedule(s => ({ ...s, work_end_time: e.target.value }))}
              className="input-field w-36"
            />
          </div>
          <button
            onClick={saveSchedule}
            disabled={schedSaving}
            className="px-4 py-2 bg-brand-500 text-white text-sm font-semibold rounded-xl hover:bg-brand-600 transition-colors disabled:opacity-50"
          >
            {schedSaved ? '✓ Saved' : schedSaving ? 'Saving…' : 'Save Schedule'}
          </button>
        </div>
      </div>

      {/* Public Holidays */}
      <div className="card p-6 space-y-4">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-bold text-brand-500 text-sm">Public Holidays</h2>
            <p className="text-xs text-gray-400 mt-0.5">These days are excluded from absence penalty calculations</p>
          </div>
          <select
            value={selectedLoc}
            onChange={e => setSelectedLoc(e.target.value)}
            className="input-field w-auto text-sm"
          >
            <option value="">All Locations</option>
            {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </div>

        <div className="flex gap-2 items-end flex-wrap">
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Date</label>
            <input type="date" value={newDate} onChange={e => setNewDate(e.target.value)} className="input-field w-40" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Holiday Name</label>
            <input
              type="text"
              placeholder="e.g. Diwali"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              className="input-field w-56"
            />
          </div>
          <button
            onClick={addHoliday}
            disabled={adding || !newDate || !newName}
            className="px-4 py-2 bg-magenta-500 text-white text-sm font-semibold rounded-xl hover:bg-magenta-600 transition-colors disabled:opacity-50"
          >
            + Add Holiday
          </button>
        </div>

        <div className="divide-y divide-brand-50 rounded-xl border border-brand-100 overflow-hidden">
          {holidays.length === 0 ? (
            <div className="py-8 text-center text-gray-400 text-sm">No holidays configured</div>
          ) : holidays.map(h => (
            <div key={h.id} className="flex items-center justify-between px-4 py-2.5 hover:bg-brand-50/40">
              <div className="flex items-center gap-4">
                <span className="font-mono text-xs text-brand-600 bg-brand-50 px-2 py-0.5 rounded">{h.date}</span>
                <span className="text-sm text-gray-700 font-medium">{h.name}</span>
                {h.location_name && <span className="text-xs text-gray-400">{h.location_name}</span>}
                {!h.location && (
                  <span className="text-xs text-amber-600 font-semibold bg-amber-50 px-1.5 py-0.5 rounded">All Locations</span>
                )}
              </div>
              <button
                onClick={() => deleteHoliday(h.id)}
                className="text-gray-300 hover:text-red-500 transition-colors text-lg leading-none"
              >×</button>
            </div>
          ))}
        </div>

        {/* Bulk Upload */}
        <div className="border-t border-brand-50 pt-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Bulk Upload (CSV)</p>
          <p className="text-xs text-gray-400 mb-3">
            CSV format: two columns — <code className="bg-gray-100 px-1 rounded">date</code> (YYYY-MM-DD) and <code className="bg-gray-100 px-1 rounded">name</code>
          </p>
          <div className="flex gap-2 items-center flex-wrap">
            <select value={uploadLoc} onChange={e => setUploadLoc(e.target.value)} className="input-field w-auto text-sm">
              <option value="">All Locations</option>
              {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
            <input
              type="file"
              accept=".csv"
              ref={fileRef}
              className="text-sm text-gray-500 file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-brand-100 file:text-brand-600 file:text-sm file:font-semibold hover:file:bg-brand-200"
            />
            <button
              onClick={handleUpload}
              disabled={uploading}
              className="px-4 py-2 bg-brand-500 text-white text-sm font-semibold rounded-xl hover:bg-brand-600 transition-colors disabled:opacity-50"
            >
              {uploading ? 'Uploading…' : 'Upload'}
            </button>
            {uploadResult && (
              <span className="text-xs text-emerald-600 font-semibold">{uploadResult.created} holidays imported</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
